import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subscription, finalize, take } from 'rxjs';

// Serviços
import { UserService } from '../../../services/user/user.service';
import { ProfileService } from 'src/app/modules/services/profile/profile.service';
import { LoadingService } from '../../loading/loading.service';
import { ToastService } from '../../../services/toast/toast.service';
import { AuthService } from 'src/app/modules/services/auth/auth.service';
import { PageSizeService } from 'src/app/modules/services/page-size/page-size.service';

// Componentes
import { ModalComponent } from '../../modal/modal.component';
import { ModalFiltersComponent } from 'src/app/modules/shared/modals/modal-filters/modal-filters.component';

@Component({
  selector: 'app-user-list',
  templateUrl: './user-list.component.html',
  styleUrls: ['./user-list.component.scss'],
})
export class UserListComponent implements OnInit, OnDestroy {
  // Configurações da Tabela
  dataSource = new MatTableDataSource<any>([]);
  displayedColumns: string[] = [];
  
  columnConfig = [
    { columnDef: 'nome', header: 'Nome', property: 'nome', customTd: true },
    { columnDef: 'email', header: 'E-mail', property: 'email' },
    { columnDef: 'profileUser', header: 'Perfil', property: 'profileUser.nome' },
    { columnDef: 'criadoEm', header: 'Data de cadastro', property: 'criadoEm', customTd: true},
    { columnDef: 'status', header: 'Status', property: 'status', customTd: true },
    { columnDef: 'actions', header: 'Ações', custom: true }
  ];

  arrayStatus: any = [
    { label: "Ativo", property: "true", selected: false },
    { label: "Inativo", property: "false", selected: false },
  ];

  // Controle de Paginação e Ordenação
  pageSize: number = 10;
  pageIndex: number = 0;
  totalElements: number = 0;
  totalPages: number = 1;
  sortState: any = { sortProperty: 'criadoEm', sortDirection: 'desc' };

  // Filtros
  filterTextValue: string = '';
  statusFilter: string = '';
  filterData: any; 
  criterios: any = [];
  criteriosLabels!: { [key: string]: string; };
  form!: FormGroup;

  // Permissões e UI
  buttonInclusaoDisabled: boolean = false;
  buttonExclusaoDisabled: boolean = false;
  buttonEdicaoDisabled: boolean = false;
  currentRoute: string = '';
  
  private subscriptions: Subscription[] = [];

  constructor(
    private router: Router,
    private userService: UserService,
    private profileService: ProfileService,
    private loadingService: LoadingService,
    private toastService: ToastService,
    public dialog: MatDialog,
    private authService: AuthService,
    private fb: FormBuilder,
    private pageSizeService: PageSizeService
  ) {
    this.currentRoute = this.router.url;
  }

  ngOnInit(): void {
    this.displayedColumns = this.columnConfig.map(col => col.columnDef);
    this.form = this.fb.group({
      perfilUsuario: ['']
    });

    this.buildCriteriosLabels();
    this.loadUserInfo();
    this.onGetUsers();
  }

  private buildCriteriosLabels() {
    this.criteriosLabels = {
      perfilUsuario: 'Perfil'
    };
  }

  onGetUsers(pageIndex: number = 0) {
    this.loadingService.showLoading();
    const f = this.filterData?.value || {};

    this.userService.getUsers(
      pageIndex,
      this.pageSize,
      this.sortState.sortDirection,
      this.sortState.sortProperty,
      this.filterTextValue, // Busca por nome ou email
      this.statusFilter,
      f.perfilUsuario || '' // ID do perfil selecionado
    )
    .pipe(
      take(1),
      finalize(() => this.loadingService.hideLoading())
    )
    .subscribe({
      next: (resp: any) => {
        const { content, pageSize, totalPages, totalElements, pageNumber } = resp;
        this.dataSource.data = content;
        this.pageSize = pageSize;
        this.totalPages = totalPages;
        this.totalElements = totalElements;
        this.pageIndex = pageNumber;
      },
      error: (erro: any) => {
        const msg = erro?.error?.message || 'Erro ao carregar usuários';
        this.toastService.showError(msg);
      }
    });
  }

  // === EVENTOS DA TABELA ===

  onSortChange(sortProperty: string): void {
    if (this.sortState.sortProperty === sortProperty) {
      this.sortState.sortDirection = this.sortState.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortState.sortProperty = sortProperty;
      this.sortState.sortDirection = 'asc';
    }
    this.onGetUsers(0);
  }

  pageChangeEvent(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.pageSizeService.setPageSize(event.pageSize);
    this.onGetUsers(event.pageIndex);
  }

  applyFilter(filterValue: string) {
    this.filterTextValue = filterValue;
    this.onGetUsers(0);
  }

  filterStatusChanged(event: any) {
    this.statusFilter = event[0]?.property || '';
    this.onGetUsers(0);
  }

  // === MODAL DE FILTROS (Se aplicável) ===
  // Nota: Como o filtro de perfil era um select search na tela, aqui ele pode ser 
  // integrado no ModalFilters seguindo o padrão dos componentes anteriores.
   openModalFilters() {
         let props;
     if(this.filterData ){
       props = this.filterData;
     }else{
       props = this.generatePropsFromForm(this.form);
     }
   
     const data = {
       type: 'user',
       propsUser: props
     };
   
     const dialogRef = this.dialog.open(ModalFiltersComponent, {
       panelClass: ['filter-panel'],
       data,
     });
   
     dialogRef.afterClosed().subscribe((res: any) => {
       if (res) {
        const formUser = res.formUser;
         this.filterData = formUser;
         this.criterios = this.getCriterios(formUser);
         this.filterData.value = {
          perfilUsuario: this.filterData.value.perfilUsuario,
          labelPerfilSelecionado: res.labelPerfilSelecionado
         }
         this.onGetUsers(0);1
       }
     });
   }
 
    // Função para gerar o objeto props de forma dinâmica a partir dos valores do formulário
  private generatePropsFromForm(form: FormGroup): any {
    const props: any = {};
    // Itera sobre os controles do FormGroup
    Object.keys(form.controls).forEach((controlName: string) => {
      const control = form.get(controlName);
  
      // Verifica se o controle tem valor e não é nulo ou vazio
      if (control && control.value !== null && control.value !== undefined && control.value !== '') {
        props[controlName] = control.value;
      }
    });
    return props;
  }
  
  getCriterios(data: any) {
    const criterios = [];
  
    // Gera os critérios de forma dinâmica, verificando os valores dos campos
    const props: any = this.generatePropsFromForm(data);
  
    for (const prop in props) {
      if (props[prop] !== null && props[prop] !== undefined && props[prop] !== '') {
        criterios.push(prop);
      }
    }
  
    return criterios;
  }

  criterioRemoved(event: any) {
    this.filterData.patchValue({ [event.criterio]: null });
    this.criterios = this.criterios.filter((c: any) => c !== event.criterio);
    this.onGetUsers(0);
  }

  // === AÇÕES E NAVEGAÇÃO ===

  create() {
    this.router.navigate(['/criar-usuario']);
  }

  redirectToEditPage(id: any) {
    this.router.navigate([`/usuario/${id}/editar`]);
  }

  openDeleteDialog(id: any): void {
    this.openConfirmDialog('Excluir', 'Você tem certeza que deseja excluir este usuário?', () => {
      this.userService.deleteUser(id).subscribe({
        next: (resp: any) => { this.toastService.showSuccess(resp.message); this.onGetUsers(); },
        error: (err) => this.toastService.showError(err?.error?.message)
      });
    });
  }

  openInactivateDialog(id: any): void {
    this.openConfirmDialog('Inativar', 'Você tem certeza que deseja inativar este usuário?', () => {
      this.userService.inactivate(id).subscribe({
        next: (resp: any) => { this.toastService.showSuccess(resp.message); this.onGetUsers(); },
        error: (err) => this.toastService.showError(err?.error?.message)
      });
    });
  }

  openActivateDialog(id: any): void {
    this.openConfirmDialog('Ativar', 'Você tem certeza que deseja ativar este usuário?', () => {
      this.userService.activate(id).subscribe({
        next: (resp: any) => { this.toastService.showSuccess(resp.message); this.onGetUsers(); },
        error: (err) => this.toastService.showError(err?.error?.message)
      });
    });
  }

  private openConfirmDialog(title: string, content: string, action: Function) {
    this.dialog.open(ModalComponent, {
      data: { modalTitle: title, modalContent: content, onPerformAction: action },
    });
  }

  // === CONTROLE DE ACESSO ===

  loadUserInfo() {
    const userId = this.authService.getUserIdFromToken();
    if (userId) {
      this.userService.getMenuUsuario(userId).subscribe((response: any) => {
        response.profileUser.permissaoPerfis.forEach((permission: any) => {
          this.checkFunctionality(permission.funcionalidade);
        });
      });
    }
  }

  checkFunctionality(functionality: any) {
    const normalize = (str: string) => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]/gi, '').toLowerCase();
    if (normalize(functionality.nome) === normalize(this.currentRoute) || functionality.url === this.currentRoute) {
      this.handleRestrictions(functionality.restricoes);
    }
    if (functionality.subFuncionalidades?.length > 0) {
      functionality.subFuncionalidades.forEach((sub: any) => this.checkFunctionality(sub));
    }
  }

  handleRestrictions(restrictions: any[]) {
    restrictions.forEach(r => {
      if (r.nome === 'Inclusão') this.buttonInclusaoDisabled = true;
      if (r.nome === 'Exclusão') this.buttonExclusaoDisabled = true;
      if (r.nome === 'Edição') this.buttonEdicaoDisabled = true;
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}